'use client';

import type { FC } from 'react';

import { useAuiState } from '@assistant-ui/react';
import { AlertCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';

import {
  resolveThreadStatusKey,
  useThreadStatusStore,
  type ThreadStatus,
} from '@/lib/thread-status/store';
import { cn } from '@/lib/utils';

// One DISTINCT color per state (both hue and motion differ, so states are unmistakable at a
// glance — Slack/Linear/Teams convention): in-flight activity pulses in the primary/accent
// color; a finished-but-unseen reply is a steady GREEN dot (done + new); needs-attention is
// amber; error is red (icon, below). Streaming vs unread previously shared the accent and were
// only distinguishable by motion — now they differ in color too.
const DOT_STYLES: Record<Exclude<ThreadStatus, 'error'>, string> = {
  streaming: 'bg-primary motion-safe:animate-pulse',
  'awaiting-approval': 'bg-amber-500 dark:bg-amber-400',
  unread: 'bg-emerald-500 dark:bg-emerald-400',
};

const STATUS_LABEL_KEYS: Record<ThreadStatus, string> = {
  streaming: 'threadList.statusStreaming',
  'awaiting-approval': 'threadList.statusAwaitingApproval',
  error: 'threadList.statusError',
  unread: 'threadList.statusUnread',
};

// Shared row-slot behavior: logical-end placement (RTL-safe) and hidden on the active row and
// wherever the row menu shows (hover/focus on fine pointers) — the menu occupies the same slot.
const SLOT_CLASSES =
  'me-2.5 shrink-0 group-data-active/thread-item:hidden pointer-fine:group-focus-within/thread-item:hidden pointer-fine:group-hover/thread-item:hidden';

/**
 * Compact status indicator at the end of a thread list row: a colored dot per state, except
 * error which renders a small alert icon — color alone is a weak signal for the one state
 * that demands action (WCAG 1.4.1 use-of-color).
 */
export const ThreadListItemStatusIndicator: FC = () => {
  const t = useTranslations();
  const statusKey = useAuiState(({ threadListItem }) =>
    threadListItem ? resolveThreadStatusKey(threadListItem) : null,
  );
  const status = useThreadStatusStore(state => (statusKey ? state.statuses[statusKey] : undefined));

  if (!status) return null;

  if (status === 'error') {
    return (
      <span
        role="status"
        aria-label={t(STATUS_LABEL_KEYS.error)}
        className={cn(SLOT_CLASSES, 'text-destructive')}
      >
        <AlertCircle aria-hidden className="size-3" />
      </span>
    );
  }

  return (
    <span
      role="status"
      aria-label={t(STATUS_LABEL_KEYS[status])}
      className={cn(SLOT_CLASSES, 'size-2 rounded-full', DOT_STYLES[status])}
    />
  );
};
